import { ChevronDown, Eye, EyeOff, Search, X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type InputHTMLAttributes, type ReactNode, type Ref, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

/* ------------------------------------------------------------------ */
/* Field frame: label + control + reserved help/error line             */
/* ------------------------------------------------------------------ */

interface FrameProps {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
  counter?: ReactNode;
}

function FieldFrame({ id, label, hint, error, optional, hideLabel, className, children, counter }: FrameProps) {
  return (
    <div className={["field", error ? "field--invalid" : "", className ?? ""].join(" ")}>
      <label htmlFor={id} className={hideLabel ? "visually-hidden" : "field__label"}>
        {label}
        {optional ? <span className="field__optional"> (optional)</span> : null}
      </label>
      {children}
      <div className="field__meta">
        {error ? (
          <p id={`${id}-error`} className="field__error">
            <span aria-hidden="true" className="field__error-mark">!</span>
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className="field__hint">
            {hint}
          </p>
        ) : null}
        {counter}
      </div>
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: ReactNode) {
  if (error) return `${id}-error`;
  if (hint) return `${id}-hint`;
  return undefined;
}

/* ------------------------------------------------------------------ */

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  hideLabel?: boolean;
  fieldClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  mono?: boolean;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({ label, hint, error, optional, hideLabel, fieldClassName, prefix, suffix, mono, id: idProp, className, ref, ...rest }: TextFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={fieldClassName}>
      <div className="field__control">
        {prefix ? <span className="field__adorn field__adorn--start">{prefix}</span> : null}
        <input
          ref={ref}
          id={id}
          className={["field-input", mono ? "field-input--mono" : "", prefix ? "has-prefix" : "", suffix ? "has-suffix" : "", className ?? ""].join(" ")}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          {...rest}
        />
        {suffix ? <span className="field__adorn field__adorn--end">{suffix}</span> : null}
      </div>
    </FieldFrame>
  );
}

/* ------------------------------------------------------------------ */

export interface MultilineFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  hideLabel?: boolean;
  fieldClassName?: string;
  minRows?: number;
  maxRows?: number;
  ref?: Ref<HTMLTextAreaElement>;
}

/** Multiline text: resize is disabled; the box grows with content up to maxRows, then scrolls. */
export function MultilineField({ label, hint, error, optional, hideLabel, fieldClassName, minRows = 3, maxRows = 14, id: idProp, className, value, ref, ...rest }: MultilineFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const inner = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const line = parseFloat(style.lineHeight) || 24;
    const chrome = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
    el.style.height = "auto";
    const min = line * minRows + chrome;
    const max = line * maxRows + chrome;
    el.style.height = `${Math.min(max, Math.max(min, el.scrollHeight + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)))}px`;
    el.style.overflowY = el.scrollHeight + 2 > max ? "auto" : "hidden";
  }, [value, minRows, maxRows]);
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={fieldClassName}>
      <textarea
        ref={(node) => {
          inner.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        id={id}
        rows={minRows}
        value={value}
        className={["field-input", "field-textarea", "resize-none", className ?? ""].join(" ")}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      />
    </FieldFrame>
  );
}

/* ------------------------------------------------------------------ */

export interface SecretFieldProps extends Omit<TextFieldProps, "type" | "suffix"> {
  /** What is hidden, for the reveal button's accessible name ("password", "API key"). */
  noun?: string;
}

export function SecretField({ noun = "password", ...props }: SecretFieldProps) {
  const [revealed, setRevealed] = useState(false);
  return (
    <TextField
      {...props}
      type={revealed ? "text" : "password"}
      spellCheck={false}
      autoCapitalize="none"
      suffix={
        <button
          type="button"
          className="field__reveal"
          aria-label={revealed ? `Hide ${noun}` : `Show ${noun}`}
          aria-pressed={revealed}
          onClick={() => setRevealed((v) => !v)}
        >
          {revealed ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
        </button>
      }
    />
  );
}

/* ------------------------------------------------------------------ */

export const SEARCH_DEBOUNCE_MS = 300;

export interface SearchFieldProps {
  label: string;
  /** Committed query (e.g. from the URL). */
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  hideLabel?: boolean;
  busy?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Search: visible input updates immediately; the committed query is debounced 300ms, suppressed
 * during IME composition, committed at once on Enter (not an IME commit) and on clear.
 */
export function SearchField({ label, value, onCommit, placeholder, hideLabel = true, busy, autoFocus, className }: SearchFieldProps) {
  const id = useId();
  const [draft, setDraft] = useState(value);
  const composing = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);
  const lastCommitted = useRef(value);

  useEffect(() => {
    // External change (Back/Forward, reset filters) wins over the draft.
    if (value !== lastCommitted.current) {
      lastCommitted.current = value;
      setDraft(value);
    }
  }, [value]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commit = (next: string) => {
    window.clearTimeout(timer.current);
    const trimmed = next.trim();
    if (trimmed === lastCommitted.current) return;
    lastCommitted.current = trimmed;
    onCommit(trimmed);
  };

  const schedule = (next: string) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => commit(next), SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className={["search-field", className ?? ""].join(" ")} role="search">
      <label htmlFor={id} className={hideLabel ? "visually-hidden" : "field__label"}>
        {label}
      </label>
      <div className="field__control">
        <span className="field__adorn field__adorn--start" aria-hidden="true">
          <Search size={20} />
        </span>
        <input
          ref={input}
          id={id}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoFocus={autoFocus}
          className="field-input has-prefix has-suffix"
          placeholder={placeholder}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (!composing.current && !(event.nativeEvent as InputEvent).isComposing) schedule(event.target.value);
          }}
          onCompositionStart={() => {
            composing.current = true;
            window.clearTimeout(timer.current);
          }}
          onCompositionEnd={(event) => {
            composing.current = false;
            schedule(event.currentTarget.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.nativeEvent.isComposing && !composing.current) {
              event.preventDefault();
              commit(draft);
            }
            if (event.key === "Escape" && draft) {
              event.preventDefault();
              setDraft("");
              commit("");
            }
          }}
        />
        <span className="field__adorn field__adorn--end">
          {busy ? <span className="search-field__busy" aria-hidden="true" /> : null}
          {draft ? (
            <button
              type="button"
              className="field__clear"
              aria-label="Clear search"
              onClick={() => {
                setDraft("");
                commit("");
                input.current?.focus();
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          ) : null}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  optional?: boolean;
  hideLabel?: boolean;
  fieldClassName?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  ref?: Ref<HTMLSelectElement>;
}

/** Native select (ownership decision: platform-owned popup; see UX-CONTRACT.md). */
export function SelectField({ label, hint, error, optional, hideLabel, fieldClassName, options, id: idProp, className, ref, ...rest }: SelectFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} optional={optional} hideLabel={hideLabel} className={fieldClassName}>
      <div className="field__control field__control--select">
        <select
          ref={ref}
          id={id}
          className={["field-input", "field-select", className ?? ""].join(" ")}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, error, hint)}
          {...rest}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="field__adorn field__adorn--end field__chevron" aria-hidden="true">
          <ChevronDown size={18} />
        </span>
      </div>
    </FieldFrame>
  );
}

/* ------------------------------------------------------------------ */

export interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  hint?: ReactNode;
  /** Same contract as the text fields: marks the box invalid and replaces the hint line. */
  error?: string;
  ref?: Ref<HTMLInputElement>;
}

export function CheckboxField({ label, hint, error, id: idProp, className, ref, ...rest }: CheckboxFieldProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  return (
    <div className={["check-field", error ? "check-field--invalid" : "", className ?? ""].join(" ")}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className="check-box"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, error, hint)}
        {...rest}
      />
      <div className="check-field__text">
        <label htmlFor={id} className="check-field__label">
          {label}
        </label>
        {error ? (
          <p id={`${id}-error`} className="field__error">
            <span aria-hidden="true" className="field__error-mark">!</span>
            {error}
          </p>
        ) : hint ? (
          <p id={`${id}-hint`} className="field__hint">
            {hint}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** Focus + scroll the first invalid control inside a form after a failed submit. */
export function focusFirstInvalid(form: HTMLElement | null) {
  if (!form) return;
  requestAnimationFrame(() => {
    const target = form.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (target) {
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: "center", behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    }
  });
}

/** Form-level error summary (server failures that are not tied to one field). */
export function FormAlert({ children, tone = "error" }: { children: ReactNode; tone?: "error" | "warning" | "info" | "success" }) {
  if (!children) return null;
  return (
    <div className={`inline-alert inline-alert--${tone}`} role={tone === "error" ? "alert" : "status"}>
      {children}
    </div>
  );
}
